import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import axios from 'axios';
import debounce from 'lodash.debounce';

const ReferringProviderAutocomplete = ({ value, onChange, placeholder }) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedValue, setSelectedValue] = useState(value || null);
  const [isWaitingForFetch, setIsWaitingForFetch] = useState(false);

  // 1. Define the core async fetching logic using useCallback
  const coreFetchLogic = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setData([]);
      setFetching(false);
      setIsWaitingForFetch(false);
      return;
    }
    setFetching(true);
    try {
      const response = await axios.get('/api/v1/emr_providers', {
        params: {
          contains: searchQuery,
        },
      });
      const providers = response.data.providers || [];
      setData(providers);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setFetching(false);
      setIsWaitingForFetch(false);
    }
  }, [setData, setFetching, setIsWaitingForFetch]);

  // 2. Create the debounced function using useMemo, depending on the memoized core logic
  const fetchProviders = useMemo(
    () => debounce(coreFetchLogic, 500),
    [coreFetchLogic],
  );

  // 3. Add cleanup effect to cancel debounce on unmount
  useEffect(() => {
    return () => {
      fetchProviders.cancel();
    };
  }, [fetchProviders]);

  // Effect to handle initial value prop
  useEffect(() => {
    setSelectedValue(value || null);
    if (value && !searchText) {
      // If we have a value but no search results, we need to fetch the provider by that value
      fetchProviders(value);
    }
  }, [value]);

  const handleSearch = (newValue) => {
    setSearchText(newValue);
    if (newValue && newValue.length >= 2) {
      setIsWaitingForFetch(true);
    } else {
      setIsWaitingForFetch(false);
      setData([]);
      fetchProviders.cancel();
    }
    fetchProviders(newValue);
  };

  const handleChange = (newValue, option) => {
    const label = option?.label || '';
    setSearchText(label);
    setSelectedValue(newValue || null);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();

    // Find the selected provider in data
    const selectedProvider = data.find((provider) => provider.id === newValue);

    // Pass both the ID and name to the parent component
    if (selectedProvider) {
      onChange(newValue, selectedProvider.name);
    } else {
      onChange(newValue || '', '');
    }
  };

  const handleClear = () => {
    setSelectedValue(null);
    setSearchText('');
    setData([]);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();
    onChange('', '');
  };

  const getNotFoundContent = () => {
    if (fetching || isWaitingForFetch) {
      return <Spin size="small" />;
    }

    if (!searchText || searchText.length < 2) {
      return 'Type to search providers';
    }

    return 'No providers found';
  };

  return (
    <Select
      showSearch
      value={selectedValue}
      placeholder={placeholder}
      defaultActiveFirstOption={false}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      notFoundContent={getNotFoundContent()}
      optionLabelProp="label"
      allowClear
      options={data.map((provider) => ({
        key: provider.id,
        value: provider.id,
        label: ['name', 'fax_number', 'address']
          .map((field) => provider[field]?.trim())
          .filter((val) => !!val)
          .join(' — '),
      }))}
    />
  );
};

ReferringProviderAutocomplete.defaultProps = {
  value: '',
};

ReferringProviderAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
};

export default ReferringProviderAutocomplete;
